import { Injectable } from '@angular/core';
import { filter } from 'rxjs/operators';
import { DomainEventBus, DomainEvent, DomainEventType } from '../events/domain-event-bus';
import { metrics, trace } from '@opentelemetry/api';
import { OTEL_SERVICE_NAME } from './telemetry.constants';
import { logs, SeverityNumber } from '@opentelemetry/api-logs';

@Injectable({ providedIn: 'root' })
export class PaymentApiTelemetryService {
  private readonly meter = metrics.getMeter(OTEL_SERVICE_NAME);
  private readonly tracer = trace.getTracer(OTEL_SERVICE_NAME);

  private readonly httpErrors = this.meter.createCounter('http_payment_errors_total', {
    description: 'HTTP errors from payment API calls'
  });
  private readonly logger = logs.getLogger(OTEL_SERVICE_NAME);

  constructor(bus: DomainEventBus) {
    // Initiate payment API errors
    bus.events$.pipe(filter((e): e is Extract<DomainEvent, { type: typeof DomainEventType.PaymentApiInitiateEnd }> => e.type === DomainEventType.PaymentApiInitiateEnd)).subscribe((e) => {
      if (!e.ok) {
        const status = typeof e.error?.status === 'number' ? e.error.status : undefined;
        const cause = status === 0 ? 'network' : typeof status === 'number' ? 'http_error' : e.error?.name === 'TimeoutError' ? 'timeout' : undefined;
        this.httpErrors.add(1, { operation: 'pay', ...(cause ? { cause } : {}) });

        this.tracer.startActiveSpan('http.payment.api.error', (span) => {
          span.setAttribute('payment.operation', 'pay');
          if (e.sessionId) span.setAttribute('session.id', e.sessionId);
          if (e.publicId) span.setAttribute('payment.public_id', e.publicId);
          if (e.correlationId) span.setAttribute('payment.correlation_id', e.correlationId);
          if (status !== undefined) span.setAttribute('payment.http_code', status);
          if (cause) span.setAttribute('error.cause', cause);

          const spanContext = span.spanContext();
          const traceId = spanContext.traceId;
          const spanId = spanContext.spanId;

          this.logger.emit({
            body: 'HTTP payment API error',
            severityNumber: SeverityNumber.ERROR,
            severityText: 'ERROR',
            attributes: {
              'session.id': e.sessionId,
              'payment.public_id': e.publicId,
              'payment.correlation_id': e.correlationId,
              'payment.http_code': status,
              'payment.operation': 'pay',
              'error.cause': cause,
              'error.name': e.error?.name,
              'error.message': e.error?.message,
              'trace.id': traceId,
              'span.id': spanId
            }
          });

          span.end();
        });
      }
    });

    // Status check API errors
    bus.events$.pipe(filter((e): e is Extract<DomainEvent, { type: typeof DomainEventType.PaymentApiStatusCheckEnd }> => e.type === DomainEventType.PaymentApiStatusCheckEnd)).subscribe((e) => {
      if (!e.ok) {
        const status = typeof e.error?.status === 'number' ? e.error.status : undefined;
        const cause = status === 0 ? 'network' : typeof status === 'number' ? 'http_error' : e.error?.name === 'TimeoutError' ? 'timeout' : undefined;
        this.httpErrors.add(1, { operation: 'getPaymentStatus', ...(cause ? { cause } : {}) });

        this.tracer.startActiveSpan('http.payment.api.error', (span) => {
          span.setAttribute('payment.operation', 'getPaymentStatus');
          if (e.sessionId) span.setAttribute('session.id', e.sessionId);
          if (e.publicId) span.setAttribute('payment.public_id', e.publicId);
          if (e.correlationId) span.setAttribute('payment.correlation_id', e.correlationId);
          if (status !== undefined) span.setAttribute('payment.http_code', status);
          if (cause) span.setAttribute('error.cause', cause);

          const spanContext = span.spanContext();
          const traceId = spanContext.traceId;
          const spanId = spanContext.spanId;

          this.logger.emit({
            body: 'HTTP payment API error',
            severityNumber: SeverityNumber.ERROR,
            severityText: 'ERROR',
            attributes: {
              'session.id': e.sessionId,
              'payment.public_id': e.publicId,
              'payment.correlation_id': e.correlationId,
              'payment.http_code': status,
              'payment.operation': 'getPaymentStatus',
              'error.cause': cause,
              'error.name': e.error?.name,
              'error.message': e.error?.message,
              'trace.id': traceId,
              'span.id': spanId
            }
          });

          span.end();
        });
      }
    });

    // Get payment request API spans/errors
    bus.events$.pipe(filter((e): e is Extract<DomainEvent, { type: typeof DomainEventType.PaymentApiGetRequestEnd }> => e.type === DomainEventType.PaymentApiGetRequestEnd)).subscribe((e) => {
      if (e.type === DomainEventType.PaymentApiGetRequestEnd) {
        if (!e.ok) {
          const status = typeof e.error?.status === 'number' ? e.error.status : undefined;
          const cause = status === 0 ? 'network' : typeof status === 'number' ? 'http_error' : e.error?.name === 'TimeoutError' ? 'timeout' : undefined;
          this.httpErrors.add(1, { operation: 'getPaymentRequest', ...(cause ? { cause } : {}) });

          this.tracer.startActiveSpan('http.payment.api.error', (span) => {
            span.setAttribute('payment.operation', 'getPaymentRequest');
            if (e.sessionId) span.setAttribute('session.id', e.sessionId);
            if (e.publicId) span.setAttribute('payment.public_id', e.publicId);
            if (status !== undefined) span.setAttribute('payment.http_code', status);
            if (cause) span.setAttribute('error.cause', cause);
            if (e.cache) span.setAttribute('cache', e.cache);

            const spanContext = span.spanContext();
            const traceId = spanContext.traceId;
            const spanId = spanContext.spanId;

            this.logger.emit({
              body: 'HTTP payment API error',
              severityNumber: SeverityNumber.ERROR,
              severityText: 'ERROR',
              attributes: {
                'session.id': e.sessionId,
                'payment.public_id': e.publicId,
                'payment.operation': 'getPaymentRequest',
                'payment.http_code': status,
                cache: e.cache,
                'error.cause': cause,
                'error.name': e.error?.name,
                'error.message': e.error?.message,
                'trace.id': traceId,
                'span.id': spanId
              }
            });

            span.end();
          });
        }
      }
    });
  }
}
